import { FC } from "react";
import { Link } from "react-router-dom";
import Icon from "../../shared/components/Icon";
import { usePageSeo } from "../../shared/components/UseSeoComponent";
import { EXTERNAL, PATHS } from "../../paths";
import { BLOG_POSTS } from "../blog/posts";
import BlogCard from "../blog/components/BlogCard";
import HeroSlider from "./components/HeroSlider";
import VideoCard from "./components/VideoCard";

const SERVICES = [
  {
    icon: "flame",
    title: "İtfaiye Hizmeti",
    text: "Saha içi sürekli yangın önleme, izleme ve profesyonel müdahale kapasitesi; NFPA standartlarıyla uyumlu ekipler.",
    to: PATHS.fire,
  },
  {
    icon: "eye",
    title: "Gözlem Hizmeti",
    text: "Yüksek riskli alanlarda kesintisiz gözetim, erken uyarı ve olay öncesi risk tespiti sağlayan yapılandırılmış izleme.",
    to: PATHS.watch,
  },
  {
    icon: "shield",
    title: "Multi Rol Güvenlik Görevlisi",
    text: "Özel güvenlik ve temel itfaiyecilik yetkinliklerini bir araya getiren Multi Rol Güvenlik Görevlileri, olası yangın risklerine karşı ilk müdahaleyi destekleyerek tesis güvenliğine katkı sağlar.",
    to: PATHS.multiRole,
  },
] as const;

const MEMBERSHIPS = [
  {
    name: "TÜYAK",
    href: "https://www.tuyak.org.tr/",
    img: "/uploads/2017/12/56d896c382fc3.png",
  },
  {
    name: "FECF",
    href: "https://www.f-e-u.org/",
    img: "/uploads/2017/12/5a2666eb26bb4.png",
  },
  {
    name: "NFPA",
    href: EXTERNAL.nfpa,
    img: "/uploads/2017/12/59c8a61f47f1f.jpg",
  },
  {
    name: "NFSA",
    href: "https://www.nfsa.org/",
    img: "/uploads/2018/08/NFSA-logo-k.png",
  },
  {
    name: "JOIFF",
    href: "https://www.joiff.com/",
    img: "/uploads/2018/08/joiff_logo.png",
  },
];

const HomePage: FC = () => {
  usePageSeo(
    "Securitas İtfaiye Hizmetleri | Yangın Güvenliği ve Acil Müdahale",
    "Securitas İtfaiye Hizmetleri A.Ş. 2011'den beri 7/24 yangın güvenliği, itfaiye hizmeti, gözlem hizmeti ve multi rol güvenlik görevlisi çözümleri sunar."
  );

  return (
    <>
      <HeroSlider />

      <section className="it-blk">
        <div className="it-wrap">
          <div className="it-sec-head">
            <span className="it-kicker">Tanıtım</span>
            <h2>Securitas İtfaiye Hizmetleri</h2>
            <p>
              Securitas, 44 ülkedeki global gücünü yerel uzmanlıkla birleştirerek
              2011'den bu yana Türkiye'de yangın güvenliği ve acil müdahale
              hizmeti veriyor.
            </p>
          </div>
          <VideoCard
            wide
            href={EXTERNAL.introVideo}
            image="/uploads/site/itfaiye-araci.jpg"
            title="İtfaiye Hizmetleri Tanıtım Filmi"
            external
          />
        </div>
      </section>

      <section className="it-blk it-blk--soft">
        <div className="it-wrap">
          <div className="it-sec-head">
            <span className="it-kicker">Gelişim</span>
            <h2>Eğitimlerimiz</h2>
          </div>
          <div className="it-two">
            <VideoCard
              href={PATHS.trainingTheory}
              image="/uploads/site/hero-multirol.jpg"
              title="Teorik ve Uygulamalı Yangın Eğitimi"
              caption="Eğitimi incele"
            />
            <VideoCard
              href={PATHS.trainingAdvanced}
              image="/uploads/site/egitim-hortum.jpg"
              title="İleri Seviye Ateşli Temel İtfaiye Eğitimi"
              caption="Eğitimi incele"
            />
          </div>
        </div>
      </section>

      <section className="it-blk">
        <div className="it-wrap">
          <div className="it-vr-box">
            <div className="it-vr-copy">
              <span className="it-kicker">Etkileşimli Eğitim</span>
              <h3>Yangında Davranış Şekilleri Simülasyonu</h3>
              <p>
                Sanal gerçeklik tabanlı simülasyon uygulamasıyla A, B ve F sınıfı
                yangın tipleriyle mücadeleyi gerçeğe mümkün olduğunca yakın,
                operasyonel seviyede verilen eğitimleri keşfedin.
              </p>
              <div className="it-chips">
                <span>A SINIFI</span>
                <span>B SINIFI</span>
                <span>F SINIFI</span>
              </div>
            </div>
            <VideoCard
              href={PATHS.trainingFireBehaviour}
              image="/uploads/site/hero-itfaiye.jpg"
              title="VR Simülasyon Deneyimi"
              caption="Eğitimi incele"
            />
          </div>
        </div>
      </section>

      <section className="it-blk it-blk--soft">
        <div className="it-wrap">
          <div className="it-sec-head">
            <span className="it-kicker">Hizmet Alanları</span>
            <h2>Hizmetlerimiz</h2>
            <p>Tesisinizin risk profiline göre yapılandırılan üç ana hizmet hattı</p>
          </div>
          <div className="it-svc-grid">
            {SERVICES.map((service) => (
              <Link className="it-svc" to={service.to} key={service.to}>
                <Icon name={service.icon} className="it-svc-ic" />
                <h3>{service.title}</h3>
                <p>{service.text}</p>
                <span className="it-more">
                  Detaylı Bilgi <Icon name="arrow" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="it-urgent">
        <div className="it-wrap">
          <div className="it-urgent-txt">
            <div className="it-big5">
              5<small>dakika</small>
            </div>
            <h2>Yangına müdahale için ilk 5 dakikanın önemi</h2>
            <Link className="it-urgent-lnk" to={PATHS.fire}>
              Öğrenmek için tıklayınız <Icon name="arrow" />
            </Link>
          </div>
        </div>
        <div className="it-urgent-ph">
          <img src="/uploads/site/itfaiye-araci.jpg" alt="Securitas İtfaiyesi aracı" />
        </div>
      </section>

      <section className="it-blk">
        <div className="it-wrap">
          <div className="it-sec-head">
            <span className="it-kicker">Bilgi Merkezi</span>
            <h2>Blog</h2>
            <p>Yangın güvenliği üzerine saha tecrübelerimiz ve uzman görüşlerimiz</p>
          </div>
          <div className="it-post-grid">
            {BLOG_POSTS.slice(0, 3).map((post) => (
              <BlogCard post={post} key={post.slug} />
            ))}
          </div>
          <div className="it-center-cta">
            <Link className="it-btn it-btn--ghost" to={PATHS.blog}>
              Tüm Yazılar
            </Link>
          </div>
        </div>
      </section>

      <section className="it-blk it-blk--soft">
        <div className="it-wrap">
          <div className="it-sec-head" style={{ marginBottom: 34 }}>
            <span className="it-kicker">Güvence</span>
            <h2>Üyeliklerimiz</h2>
          </div>
          <div className="it-members">
            {MEMBERSHIPS.map((member) => (
              <a
                key={member.name}
                href={member.href}
                target="_blank"
                rel="noopener noreferrer"
                title={member.name}
              >
                <img src={member.img} alt={member.name} />
              </a>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
